import { DayEntry, PlannedPunch, PunchType, ScheduleEvent, ScheduleShift } from '../types';

/** Matches a strict `HH:MM` 24h clock time — used to flag an empty or invalid time in a plan line. */
export const HH_MM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const PUNCH_TYPES: PunchType[] = ['entry', 'break_start', 'break_end', 'exit'];

export const minutesToHHMM = (minutes: number): string => {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const hh = Math.floor(wrapped / 60)
    .toString()
    .padStart(2, '0');
  const mm = (wrapped % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
};

/** Parses an `HH:MM` string to minutes from midnight. Null when the string is not exactly that shape. */
export const hhmmToMinutes = (hhmm: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return null;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

export const localDateOfPunch = (deviceDatetime: string, timeZone: string): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  return formatter.format(new Date(deviceDatetime));
};

/** The local clock time (HH:MM, 24h) of a UTC `deviceDatetime` string in the given IANA time zone — used to render a punch chip's time. */
export const localTimeOfPunch = (deviceDatetime: string, timeZone: string): string => {
  const formatter = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return formatter.format(new Date(deviceDatetime));
};

/** Shifts `date` (YYYY-MM-DD) forward by `days` calendar days. */
const shiftDate = (date: string, days: number): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

interface EventPlacement {
  date: string;
  time: string;
}

const OFFSET_BY_TYPE: Record<PunchType, 'entryOffset' | 'exitOffset' | 'breakStartOffset' | 'breakEndOffset'> = {
  entry: 'entryOffset',
  exit: 'exitOffset',
  break_start: 'breakStartOffset',
  break_end: 'breakEndOffset'
};

/** Per-type minute offsets applied to every event's computed placement — fixed-time events and flexible/duration breaks alike. */
export interface ScheduleBuildOptions {
  includeBreaks: boolean;
  skipDaysWithPunches: boolean;
  entryOffset: number;
  breakStartOffset: number;
  breakEndOffset: number;
  exitOffset: number;
  /** HH:MM local start used for shifts whose events have no fixed time (flexible schedules). */
  flexibleStart: string;
}

/** Default minutes to use for a flexible break with no `duration` of its own. */
export const DEFAULT_FLEXIBLE_BREAK_MINUTES = 30;

/** Default value for the "Flexible shift start" field. */
export const DEFAULT_FLEXIBLE_START = '09:00';

/** A shift whose events all lack a fixed time. It can only be planned from a chosen start time. */
export const isFlexibleShift = (shift: Pick<ScheduleShift, 'events'>): boolean => shift.events.length > 0 && shift.events.every((e) => e.time === null);

/** Working minutes of a flexible shift: a positive `shiftDuration`, else a positive exit-event `duration`, else null (cannot plan). Zero means "not set" here, so it falls through. */
export const flexibleWorkingMinutes = (shift: Pick<ScheduleShift, 'events' | 'shiftDuration'>): number | null => {
  if (typeof shift.shiftDuration === 'number' && shift.shiftDuration > 0) return shift.shiftDuration;
  const exitDuration = shift.events.find((e) => e.type === 'exit')?.duration;
  return typeof exitDuration === 'number' && exitDuration > 0 ? exitDuration : null;
};

/** True when at least one working shift in `days` is flexible — the dialog shows the start-time field only then. */
export const hasFlexibleShift = (days: Pick<DayEntry, 'shifts'>[]): boolean => days.some((day) => day.shifts.some((shift) => shift.working && isFlexibleShift(shift)));

/** Formats a minutes-from-`baseDate`-midnight value as a placement, rolling the date forward for values >= 1440 (or back for negative values). */
const placeMinutes = (baseDate: string, totalMinutes: number): EventPlacement => {
  const dayShift = Math.floor(totalMinutes / 1440);
  return { date: shiftDate(baseDate, dayShift), time: minutesToHHMM(totalMinutes) };
};

/** Placement for a fixed-time event: the event's own `time` (minutes from shift-date midnight) plus its offset. */
const placeFixedEvent = (baseDate: string, event: ScheduleEvent, offsetMinutes: number): EventPlacement => placeMinutes(baseDate, (event.time ?? 0) + offsetMinutes);

/** Unoffset start, in minutes from `baseDate` midnight, for a flexible/duration break: `startAfter` from shift start, else the shift's middle. Null when no event in the shift has a fixed clock time — there is nothing to anchor the break to, so the caller should skip it. */
const flexibleBreakStartMinutes = (events: ScheduleEvent[], event: ScheduleEvent): number | null => {
  const clockEvents = events.filter((e) => e.time !== null);
  if (clockEvents.length === 0) return null;
  const shiftStart = Math.min(...clockEvents.map((e) => e.time as number));
  const shiftEnd = Math.max(...clockEvents.map((e) => e.time as number));
  return event.startAfter !== undefined ? shiftStart + event.startAfter : Math.round((shiftStart + shiftEnd) / 2);
};

export const buildFromSchedule = (days: Pick<DayEntry, 'date' | 'shifts' | 'punches'>[], options: ScheduleBuildOptions): PlannedPunch[] => {
  const planned: PlannedPunch[] = [];

  for (const day of days) {
    if (options.skipDaysWithPunches && day.punches.length > 0) continue;

    for (const shift of day.shifts) {
      if (!shift.working) continue;

      if (isFlexibleShift(shift)) {
        const working = flexibleWorkingMinutes(shift);
        const start = hhmmToMinutes(options.flexibleStart);
        if (working === null || start === null) continue; // cannot plan -> skipped, reason hint covers it

        const entryEvent = shift.events.find((e) => e.type === 'entry');
        if (!entryEvent) continue; // no entry to anchor the shift to -> skip it entirely

        // Pair each break_start with the next break_end in event order; an unmatched break_start is dropped.
        const breakStarts = shift.events.filter((e) => e.type === 'break_start');
        const breakEnds = shift.events.filter((e) => e.type === 'break_end');
        const pairs: [ScheduleEvent, ScheduleEvent][] = breakStarts.map((bs, i) => [bs, breakEnds[i]]).filter((pair): pair is [ScheduleEvent, ScheduleEvent] => pair[1] !== undefined);

        const included = options.includeBreaks ? pairs : [];
        const durations = included.map(([bs]) => bs.duration ?? DEFAULT_FLEXIBLE_BREAK_MINUTES);

        const entryPlacement = placeMinutes(day.date, start + options.entryOffset);
        planned.push({ date: entryPlacement.date, time: entryPlacement.time, punchType: 'entry', shiftId: shift.shiftId, shiftEventKey: entryEvent.key });

        let consumed = 0;
        included.forEach(([bs, be], i) => {
          const rawStart = start + Math.round((working * (i + 1)) / (included.length + 1)) + consumed;
          const startPlacement = placeMinutes(day.date, rawStart + options.breakStartOffset);
          planned.push({ date: startPlacement.date, time: startPlacement.time, punchType: 'break_start', shiftId: shift.shiftId, shiftEventKey: bs.key });
          const endPlacement = placeMinutes(day.date, rawStart + durations[i] + options.breakEndOffset);
          planned.push({ date: endPlacement.date, time: endPlacement.time, punchType: 'break_end', shiftId: shift.shiftId, shiftEventKey: be.key });
          consumed += durations[i];
        });

        const exitEvent = shift.events.find((e) => e.type === 'exit');
        if (exitEvent) {
          const exitPlacement = placeMinutes(day.date, start + working + consumed + options.exitOffset);
          planned.push({ date: exitPlacement.date, time: exitPlacement.time, punchType: 'exit', shiftId: shift.shiftId, shiftEventKey: exitEvent.key });
        }

        continue;
      }

      // Unoffset break-start minutes, kept for the matching break_end's base (which must NOT include breakStartOffset).
      let rawBreakStartMinutes: number | undefined;

      for (const event of shift.events) {
        if ((event.type === 'break_start' || event.type === 'break_end') && !options.includeBreaks) continue;

        const offsetMinutes = options[OFFSET_BY_TYPE[event.type]];
        let placement: EventPlacement;

        if (event.time === null) {
          if (event.type === 'break_start') {
            const rawStart = flexibleBreakStartMinutes(shift.events, event);
            if (rawStart === null) continue; // no fixed-time event in this shift to anchor the break to
            rawBreakStartMinutes = rawStart;
            placement = placeMinutes(day.date, rawStart + offsetMinutes);
          } else {
            // break_end with no fixed time: unoffset start + duration + breakEndOffset
            const rawStart = rawBreakStartMinutes ?? flexibleBreakStartMinutes(shift.events, event);
            if (rawStart === null) continue;
            placement = placeMinutes(day.date, rawStart + (event.duration ?? 0) + offsetMinutes);
          }
        } else {
          placement = placeFixedEvent(day.date, event, offsetMinutes);
        }

        planned.push({ date: placement.date, time: placement.time, punchType: event.type, shiftId: shift.shiftId, shiftEventKey: event.key });
      }
    }
  }

  return planned;
};

/** Distinct SOURCE dates (the shift's own `day.date`, not the placed punch date) that would contribute
 * at least one punch under `buildFromSchedule`'s rules. A night shift's exit rolling onto the next
 * calendar date must not count that one shift as two days — count by origin day, not placed date or shiftId. */
export const countSourceDays = (days: Pick<DayEntry, 'date' | 'shifts' | 'punches'>[], options: Pick<ScheduleBuildOptions, 'skipDaysWithPunches' | 'includeBreaks'>): number => {
  let count = 0;
  for (const day of days) {
    if (options.skipDaysWithPunches && day.punches.length > 0) continue;
    const producesPunch = day.shifts.some((shift) => {
      if (!shift.working) return false;
      if (isFlexibleShift(shift)) return flexibleWorkingMinutes(shift) !== null && shift.events.some((event) => event.type === 'entry');
      return shift.events.some((event) => (options.includeBreaks || (event.type !== 'break_start' && event.type !== 'break_end')) && event.time !== null);
    });
    if (producesPunch) count++;
  }
  return count;
};

export type ScheduleEmptyReason = 'no-days' | 'all-days-have-punches' | 'no-working-shifts' | 'no-fixed-times' | 'only-breaks-excluded' | 'bad-flexible-start';

/** Why `buildFromSchedule` would return nothing for these days, or null when it would return at least one punch. Mirrors buildFromSchedule's skip rules, checked in order. */
export const scheduleEmptyReason = (days: Pick<DayEntry, 'date' | 'shifts' | 'punches'>[], options: ScheduleBuildOptions): ScheduleEmptyReason | null => {
  if (buildFromSchedule(days, options).length > 0) return null;

  if (days.length === 0) return 'no-days';

  if (options.skipDaysWithPunches && days.every((day) => day.punches.length > 0)) return 'all-days-have-punches';

  const remainingDays = options.skipDaysWithPunches ? days.filter((day) => day.punches.length === 0) : days;
  // A working shift with no events has nothing to build from either — treat it the same as no working shift.
  const workingShifts = remainingDays.flatMap((day) => day.shifts).filter((shift) => shift.working && shift.events.length > 0);

  if (workingShifts.length === 0) return 'no-working-shifts';

  // A flexible shift that could otherwise be planned, blocked only by an unparsable start time.
  const plannableFlexibleShifts = workingShifts.filter((shift) => isFlexibleShift(shift) && flexibleWorkingMinutes(shift) !== null && shift.events.some((event) => event.type === 'entry'));
  if (plannableFlexibleShifts.length > 0 && hhmmToMinutes(options.flexibleStart) === null) return 'bad-flexible-start';

  const nonExcludedEvents = workingShifts.flatMap((shift) => shift.events).filter((event) => options.includeBreaks || (event.type !== 'break_start' && event.type !== 'break_end'));

  if (!options.includeBreaks && nonExcludedEvents.length === 0) return 'only-breaks-excluded';

  if (nonExcludedEvents.length > 0 && nonExcludedEvents.every((event) => event.time === null)) return 'no-fixed-times';

  return null;
};

export interface CustomPunchRow {
  time: string;
  punchType: PunchType;
}

export interface CustomBuildOptions {
  skipDaysWithPunches: boolean;
  weekdaysOnly: boolean;
}

const WEEKDAY = new Set([1, 2, 3, 4, 5]);

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export type CustomDayStatus = 'planned' | 'skipped-weekend' | 'skipped-has-punches';

export interface CustomDay {
  date: string;
  status: CustomDayStatus;
}

/** Every date in the range, in order, with why it is skipped (or 'planned'). Same rules as buildCustom. Empty for an invalid/reversed range. */
export const listCustomDays = (range: { from: string; to: string }, options: CustomBuildOptions, days: Pick<DayEntry, 'date' | 'punches'>[]): CustomDay[] => {
  if (!DATE_RE.test(range.from) || !DATE_RE.test(range.to) || range.from > range.to) return [];

  const punchesByDate = new Map(days.map((d) => [d.date, d.punches]));
  const result: CustomDay[] = [];

  for (let cursor = range.from; cursor <= range.to; cursor = shiftDate(cursor, 1)) {
    const weekday = new Date(`${cursor}T00:00:00Z`).getUTCDay();
    if (options.weekdaysOnly && !WEEKDAY.has(weekday)) {
      result.push({ date: cursor, status: 'skipped-weekend' });
      continue;
    }
    if (options.skipDaysWithPunches && (punchesByDate.get(cursor)?.length ?? 0) > 0) {
      result.push({ date: cursor, status: 'skipped-has-punches' });
      continue;
    }
    result.push({ date: cursor, status: 'planned' });
  }

  return result;
};

/** Groups a plan by date, keeping line order. */
export const groupByDate = (plan: PlannedPunch[]): Map<string, PlannedPunch[]> => {
  const grouped = new Map<string, PlannedPunch[]>();
  for (const line of plan) {
    const existing = grouped.get(line.date);
    if (existing) existing.push(line);
    else grouped.set(line.date, [line]);
  }
  return grouped;
};

/** True when a day's lines differ from the template rows (compared as ordered time+type pairs). */
export const isDayEdited = (lines: PlannedPunch[], rows: CustomPunchRow[]): boolean => {
  if (lines.length !== rows.length) return true;
  return lines.some((line, i) => line.time !== rows[i].time || line.punchType !== rows[i].punchType);
};

/** Stable sort by date then time; used right before submit so edits typed out of order still create in order. */
export const sortPlan = (plan: PlannedPunch[]): PlannedPunch[] =>
  [...plan].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.time !== b.time) return a.time < b.time ? -1 : 1;
    return 0;
  });

export const buildCustom = (range: { from: string; to: string }, rows: CustomPunchRow[], options: CustomBuildOptions, days: Pick<DayEntry, 'date' | 'punches'>[]): PlannedPunch[] => {
  const planned: PlannedPunch[] = [];

  for (const day of listCustomDays(range, options, days)) {
    if (day.status !== 'planned') continue;
    for (const row of rows) {
      planned.push({ date: day.date, time: row.time, punchType: row.punchType });
    }
  }

  return planned;
};
